import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, GitBranch, Inbox, FileText, RefreshCw, ShieldCheck,
  BellRing, DollarSign, Award, Users, Settings as SettingsIcon, LogOut, Menu, X, Upload,
  Rocket, AlertTriangle, KeyRound, Send, Phone, Gauge, MonitorPlay, ListChecks, Megaphone, Building2, CalendarClock,
  ChevronDown, ChevronRight, Headphones,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { usePresentation } from "@/context/PresentationContext";
import { PresentationToggle } from "@/components/PresentationToggle";
import { GlobalSearch } from "@/components/GlobalSearch";
import { Copilot } from "@/components/Copilot";
import { GuidedTour } from "@/components/GuidedTour";
import { CopilotNamingGate } from "@/components/CopilotNaming";
import { useTranslation } from "react-i18next";
import { setLanguage } from "@/i18n";
import { Languages } from "lucide-react";
import { UniversalIntake } from "@/components/UniversalIntake";
import { RestrictedVault } from "@/components/RestrictedVault";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const NAV_TKEY = {
  "/": "nav.dashboard", "/command-center": "nav.command_center", "/queue": "nav.work_queue",
  "/pipeline": "nav.lead_pipeline", "/intake": "nav.intake_queue", "/lead-review": "nav.lead_review",
  "/growth": "nav.growth", "/vendor": "nav.submit_leads", "/appointments": "nav.appointments",
  "/telephony": "nav.telephony", "/quotes": "nav.quotes", "/requote": "nav.pending",
  "/policies": "nav.approved", "/followups": "nav.followups", "/commissions": "nav.commissions",
  "/scorecard": "nav.scorecard",
  "/import": "nav.import", "/recovery": "nav.recovery", "/vault": "nav.credentials", "/credential-review": "nav.credential_review",
  "/team": "nav.team", "/agency-admin": "nav.agency_admin", "/onboarding": "nav.onboarding",
  "/settings": "nav.settings",
};

const NAV = [
  { to: "/platform", label: "Platform", icon: Building2, onlyFor: ["platform_owner"] },
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/command-center", label: "Agency Command Center", icon: Gauge, onlyFor: ["admin", "ops_manager"] },
  { to: "/growth", label: "Growth Engine", icon: Megaphone, onlyFor: ["admin", "ops_manager", "agent"] },
  { to: "/import", label: "Import Data", icon: Upload, onlyFor: ["admin", "agent", "bpo_closer"] },
  { to: "/recovery", label: "Existing Book Recovery", icon: RefreshCw, onlyFor: ["admin", "ops_manager", "agent"] },
  { to: "/vendor", label: "Submit Leads", icon: Send, onlyFor: ["vendor"] },
  { to: "/intake", label: "Intake Queue", icon: Inbox },
  { to: "/lead-review", label: "Lead Review", icon: Inbox, onlyFor: ["admin", "ops_manager"] },
  { to: "/pipeline", label: "Lead Pipeline", icon: GitBranch },
  { to: "/queue", label: "Work Queue", icon: ListChecks, hideFor: ["future"] },
  { to: "/telephony", label: "Telephony", icon: Phone, hideFor: ["bpo", "future"] },
  { to: "/sophia", label: "Sophia Operations", icon: Headphones, onlyFor: ["admin", "ops_manager", "agent", "bpo_closer"] },
  { to: "/appointments", label: "Appointments", icon: CalendarClock, hideFor: ["bpo", "bpo_closer", "future"] },
  { to: "/quotes", label: "Quotes & Applications", icon: FileText },
  { to: "/requote", label: "Pending / Requote", icon: RefreshCw },
  { to: "/policies", label: "Approved Policies", icon: ShieldCheck },
  { to: "/followups", label: "Follow-Up Queue", icon: BellRing },
  { to: "/commissions", label: "Commissions", icon: DollarSign, hideFor: ["bpo", "bpo_closer", "future"] },
  { to: "/scorecard", label: "Carrier & Vendor Scorecard", icon: Award, hideFor: ["bpo", "bpo_closer", "future"] },
  { to: "/team", label: "Team", icon: Users },
  { to: "/agency-admin", label: "Agency Admin", icon: ShieldCheck, onlyFor: ["admin", "ops_manager"] },
  { to: "/vault", label: "Credential Vault", icon: KeyRound, hideFor: ["bpo", "bpo_closer", "future"] },
  { to: "/credential-review", label: "Credential Review", icon: ShieldCheck, onlyFor: ["admin", "ops_manager"] },
  { to: "/onboarding", label: "Onboarding", icon: Rocket },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

const GROUP_ROUTES = {
  growth: ["/growth", "/import", "/recovery", "/intake", "/lead-review"],
  business: ["/telephony", "/appointments", "/requote", "/policies", "/commissions", "/scorecard"],
  tools: ["/team", "/agency-admin", "/vault", "/credential-review", "/onboarding", "/settings"],
};

const GROUP_COPY = {
  en: { growth: "Grow & Intake", business: "Policies & Money", tools: "Tools & Setup" },
  es: { growth: "Crecimiento y Admisión", business: "Pólizas y Dinero", tools: "Herramientas y Configuración" },
};

const ROLE_ES = {
  admin: "Administrador",
  ops_manager: "Gerente de Operaciones",
  agent: "Agente con Licencia",
  bpo: "Admisión BPO",
  bpo_closer: "Admisión BPO + Llamadas",
  future: "Agente en Formación",
  platform_owner: "Propietario de Plataforma",
};

export function Layout({ children }) {
  const { user, logout, refreshUser } = useAuth();
  const { t, i18n } = useTranslation();
  const { isDemo } = usePresentation();
  const isOwnerAdmin = ["admin", "ops_manager"].includes(user.role);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [pilot, setPilot] = useState(false);
  const lang = i18n.language?.startsWith("es") ? "es" : "en";

  const exitAgencyView = async () => {
    try { await api.post("/platform/impersonation/exit"); await refreshUser(); navigate("/platform"); }
    catch (_) { window.location.assign("/platform"); }
  };

  useEffect(() => {
    api.get("/settings").then(({ data }) => setPilot(!!data.pilot_mode)).catch(() => {});
  }, []);

  const nav = (user.role === "vendor"
    ? NAV.filter((n) => n.onlyFor?.includes("vendor"))
    : user.role === "platform_owner"
    ? NAV.filter((n) => n.onlyFor?.includes("platform_owner"))
    : NAV.filter((n) => !n.hideFor?.includes(user.role) && (!n.onlyFor || n.onlyFor.includes(user.role)))
  ).map((n) => (n.to === "/" && user.role === "agent" ? { ...n, label: "My Command Center", icon: Gauge } : n));

  const quickRoutes = user.role === "bpo_closer"
    ? ["/telephony", "/import", "/pipeline", "/queue", "/followups"]
    : user.role === "agent"
    ? ["/", "/queue", "/pipeline", "/followups", "/quotes"]
    : ["/", "/command-center", "/queue", "/pipeline", "/followups"];
  const quick = user.role === "bpo_closer"
    ? quickRoutes.map((r) => nav.find((n) => n.to === r)).filter(Boolean)
    : nav.filter((n) => quickRoutes.includes(n.to));
  const grouped = Object.fromEntries(Object.entries(GROUP_ROUTES).map(([key, routes]) => [
    key,
    nav.filter((n) => routes.includes(n.to) && !quickRoutes.includes(n.to)),
  ]));
  const leftovers = nav.filter((n) => !quickRoutes.includes(n.to) && !Object.values(GROUP_ROUTES).flat().includes(n.to));

  const activeGroup = Object.entries(GROUP_ROUTES).find(([, routes]) => routes.includes(location.pathname))?.[0] || null;
  const [openGroups, setOpenGroups] = useState(() => ({ growth: false, business: false, tools: false }));
  useEffect(() => {
    if (activeGroup) setOpenGroups((g) => ({ ...g, [activeGroup]: true }));
  }, [activeGroup]);

  const navText = (n) => {
    if (user.role === "agent" && n.to === "/") return lang === "es" ? "Mi Centro de Mando" : "My Command Center";
    return t(NAV_TKEY[n.to] || "_", { defaultValue: n.label });
  };

  const NavItem = ({ n, nested = false }) => (
    <NavLink
      key={n.to}
      to={n.to}
      end={n.end}
      onClick={() => setOpen(false)}
      data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
      className={({ isActive }) => cn(
        "group flex items-center gap-3 rounded-md text-[13px] font-medium transition-colors duration-150 border-l-2 border-transparent",
        nested ? "px-3 py-1.5 ml-2" : "px-3 py-2",
        isActive ? "bg-white/10 text-white border-l-[#B28A3D]" : "text-slate-300 hover:text-white hover:bg-white/5"
      )}
    >
      <n.icon className={`${nested ? "h-4 w-4" : "h-[18px] w-[18px]"} shrink-0`} />
      <span className="truncate">{navText(n)}</span>
    </NavLink>
  );

  const NavGroup = ({ groupKey }) => {
    const items = grouped[groupKey] || [];
    if (!items.length) return null;
    const expanded = !!openGroups[groupKey];
    return (
      <div className="pt-1">
        <button
          onClick={() => setOpenGroups((g) => ({ ...g, [groupKey]: !g[groupKey] }))}
          data-testid={`nav-group-${groupKey}`}
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400 hover:bg-white/5 hover:text-slate-200"
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          <span className="flex-1 truncate">{GROUP_COPY[lang][groupKey]}</span>
          <span className="rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-slate-400">{items.length}</span>
        </button>
        {expanded && <div className="mt-0.5 space-y-0.5">{items.map((n) => <NavItem key={n.to} n={n} nested />)}</div>}
      </div>
    );
  };

  const SidebarInner = () => (
    <div className="flex h-full flex-col bg-[#17324D] text-slate-100">
      <div className="px-4 py-4 border-b border-white/10">
        <img src="/fo-logo-light-trimmed.png" alt="Fortunate Operations" className="w-[190px] h-auto max-w-full object-contain" />
        <div className="mt-2 text-center text-[10px] uppercase tracking-[0.22em] text-slate-300">Agency OS</div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-3 kanban-scroll" data-testid="condensed-navigation">
        <div className="space-y-0.5">{quick.map((n) => <NavItem key={n.to} n={n} />)}</div>
        <div className="my-2 border-t border-white/10" />
        <NavGroup groupKey="growth" />
        <NavGroup groupKey="business" />
        <NavGroup groupKey="tools" />
        {leftovers.length > 0 && <div className="mt-2 space-y-0.5">{leftovers.map((n) => <NavItem key={n.to} n={n} />)}</div>}
      </nav>
      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-3 rounded-md px-2 py-2">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-[#B28A3D]/20 text-[#F5E8C8] text-xs font-semibold ring-1 ring-white/10">
            {user.avatar || user.name?.[0]}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-medium text-white">{user.name}</div>
            <div className="truncate text-[11px] text-slate-400">
              {user.impersonating
                ? (lang === "es" ? "Propietario · Vista de Agencia" : "Platform Owner · Agency View")
                : (lang === "es" ? (ROLE_ES[user.role] || ROLE_LABELS[user.role]) : ROLE_LABELS[user.role])}
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className="rounded-md p-1.5 text-slate-400 hover:text-white hover:bg-white/10"
            title={lang === "es" ? "Cerrar sesión" : "Sign out"}
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col z-30">
        <SidebarInner />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64"><SidebarInner /></div>
        </div>
      )}

      <div className={`lg:pl-64 min-h-screen ${isDemo ? "bg-[#B28A3D]/[0.04]" : ""}`}>
        <header className={`sticky top-0 z-20 flex h-14 items-center gap-3 border-b px-4 backdrop-blur lg:px-6 ${isDemo ? "border-[#B28A3D]/40 bg-[#B28A3D]/10" : "border-border bg-white/80"}`}>
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <div className="flex-1" />
          {user.role !== "vendor" && <GlobalSearch />}
          <div className="flex items-center gap-1 rounded-full border bg-white px-2 py-1" data-testid="language-selector">
            <Languages className="h-3.5 w-3.5 text-muted-foreground" />
            <select
              value={lang}
              onChange={async (e) => {
                setLanguage(e.target.value);
                try { await api.put("/auth/language", { language: e.target.value }); } catch (_) {}
              }}
              data-testid="language-select"
              className="bg-transparent text-xs outline-none">
              <option value="en">EN</option>
              <option value="es">ES</option>
            </select>
          </div>
          {isOwnerAdmin && <PresentationToggle />}
          <div className="hidden sm:flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-800 ring-1 ring-amber-200" data-testid="pilot-badge">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" /> Pilot Mode
          </div>
        </header>
        {isDemo && (
          <div className="flex items-center gap-2 border-b border-[#B28A3D]/30 bg-[#B28A3D]/10 px-4 py-2 text-[12px] font-medium text-[#7a5d27] lg:px-6" data-testid="demo-banner">
            <MonitorPlay className="h-4 w-4 shrink-0" />
            <span><strong>Presentation Mode — Demo / Sample Data.</strong> All figures shown are a fictional sample agency (Summit Shield Insurance Group). No live Fortunate Operations, client, credential, or telephony data is displayed.</span>
          </div>
        )}
        {user.impersonating && !isDemo && (
          <div className="flex items-center justify-between gap-3 border-b border-sky-200 bg-sky-50 px-4 py-2 text-[12px] text-sky-900 lg:px-6" data-testid="impersonation-banner">
            <span><strong>Platform Owner agency view:</strong> {user.impersonated_agency_name}. Actions are live and recorded in the audit log.</span>
            <Button size="sm" variant="outline" onClick={exitAgencyView} data-testid="exit-agency-view">Return to Platform</Button>
          </div>
        )}
        {pilot && !isDemo && (
          <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-[12px] text-amber-800 lg:px-6" data-testid="pilot-banner">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span><strong>Pilot Mode — Internal Testing.</strong> Demo data only. Do not enter highly sensitive client data until security, privacy, retention &amp; compliance requirements are formally reviewed.</span>
          </div>
        )}
        <main className="p-4 lg:p-6" data-testid="app-main">{children}</main>
      </div>
      {user.role !== "vendor" && <Copilot />}
      {user.role !== "vendor" && <GuidedTour />}
      {user.role !== "vendor" && <UniversalIntake />}
      {user.role !== "vendor" && <RestrictedVault />}
      <CopilotNamingGate />
    </div>
  );
}
